import { useEffect, useState } from 'react'
import ReactFC from 'react-fusioncharts'
import { Typography } from '@material-ui/core'
import { send_command } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { heartbeatHourlyToChart } from 'utils/heartbeatHourlyToChart'
import { parseUtcTimestamp } from 'utils/formatUtcTimestamp'

function formatDayLabel(date) {
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatHourLabel(date) {
  return date.toLocaleTimeString(undefined, { hour: 'numeric' })
}

function buildAxisLabels(rows, { compact, days }) {
  const dates = rows.map(([time]) => parseUtcTimestamp(time) || new Date(time))

  if (compact && days > 1) {
    const seenDays = new Set()
    return dates.map((date) => {
      const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
      if (seenDays.has(dayKey)) return ''
      seenDays.add(dayKey)
      return formatDayLabel(date)
    })
  }

  const targetLabels = compact ? 6 : 12
  const step = Math.max(1, Math.ceil(rows.length / targetLabels))
  return dates.map((date, index) => {
    if (index % step !== 0 && index !== rows.length - 1) return ''
    return compact
      ? formatHourLabel(date)
      : `${formatDayLabel(date)} ${formatHourLabel(date)}`
  })
}

export function HeartbeatChart({ tenantId, tenantName, days = 3, height, compact = false }) {
  const chartHeight = height ?? (compact ? 180 : 400)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [chartConfig, setChartConfig] = useState(null)

  const applyChartRows = (rows) => {
    if (!rows || rows.length === 0) {
      setChartConfig(null)
      setError(null)
      setLoading(false)
      return
    }
    const axisLabels = buildAxisLabels(rows, { compact, days })
    setChartConfig({
      type: 'line',
      width: '100%',
      height: String(chartHeight),
      dataFormat: 'json',
      dataSource: {
        chart: {
          caption: compact ? '' : 'Heartbeats per hour',
          subCaption: compact ? '' : (tenantName || ''),
          xAxisName: compact ? '' : 'Time',
          yAxisName: compact ? '' : 'Count',
          theme: 'fusion',
          labelDisplay: compact ? 'auto' : 'rotate',
          slantLabel: compact ? '0' : '1',
          staggerLines: '1',
          showValues: '0',
          showBorder: compact ? '0' : '1',
          showLegend: '0',
          captionFontSize: compact ? '12' : '14',
          chartLeftMargin: compact ? '8' : '20',
          chartRightMargin: compact ? '8' : '20',
          chartTopMargin: compact ? '4' : '10',
          chartBottomMargin: compact ? '24' : '10',
        },
        data: rows.map(([, count], index) => ({
          label: axisLabels[index],
          value: String(count),
        })),
      },
    })
    setError(null)
    setLoading(false)
  }

  const load = () => {
    if (!tenantId) return
    setLoading(true)
    send_command('ADMIN_GET_LATEST_HEARTBEATS_RAW', { tenant_id: tenantId })
      .then((raw) => {
        applyChartRows(heartbeatHourlyToChart(raw, days))
      })
      .catch((err) => {
        setError(getApiErrorMessage(err, 'Failed to load heartbeat chart'))
        setChartConfig(null)
        setLoading(false)
      })
  }

  useEffect(() => {
    load()
  }, [tenantId, days])

  if (loading && !chartConfig) {
    return <Typography color="textSecondary">Loading chart…</Typography>
  }
  if (error) {
    return <Typography color="error" variant="body2">{error}</Typography>
  }
  if (!chartConfig) {
    return (
      <Typography color="textSecondary" variant="body2">
        No heartbeat data in the last {days} day{days === 1 ? '' : 's'}.
      </Typography>
    )
  }
  return <ReactFC {...chartConfig} />
}
